import createAsyncAction from "utils/createAsyncAction";
import { setMessage } from "actions/message";
import Log from "utils/log";
import sanitizeSlug from "utils/sanitizeSlug";
import { logLocally } from "utils/localLogging";
import { UUID_REGEXP } from "config/constants";
import { getContentAsync, GET_CONTENT_FAILED } from "actions/items";
import validate from "deepDiveEditor/utils/validate";
import { VALIDATION_MESSAGE } from "config/constants/editor";

export const INITIALIZE_DEEP_DIVE_FORM = "INITIALIZE_DEEP_DIVE_FORM";
export const LOAD_DEEP_DIVE_STORIES = "LOAD_DEEP_DIVE_STORIES";

export const GET_DEEP_DIVE_REQUESTED = "GET_DEEP_DIVE_REQUESTED";
export const GET_DEEP_DIVE_SUCCEEDED = "GET_DEEP_DIVE_SUCCEEDED";
export const GET_DEEP_DIVE_FAILED = "GET_DEEP_DIVE_FAILED";

export const getDeepDiveAsync = id =>
  createAsyncAction("deepDives", "read", "GET_DEEP_DIVE")(id);

export const initializeDeepDiveForm = payload => {
  return {
    type: INITIALIZE_DEEP_DIVE_FORM,
    payload,
  };
};

export const loadDeepDiveStories = payload => {
  return {
    type: LOAD_DEEP_DIVE_STORIES,
    payload,
  };
};

export const addDeepDiveStory = stories => dispatch => action => {
  dispatch(loadDeepDiveStories([action.payload]));
  dispatch(onChangeStories([...stories, action.payload.id]));
};

export const addDeepDiveCoverStory = action => dispatch => {
  dispatch(loadDeepDiveStories([action.payload]));
  onChangeField("front_cover")(dispatch)(action.payload.id);
};

export const hydrateDeepDiveForm = id => {
  return dispatch => {
    return dispatch(getDeepDiveAsync(id)).then(action => {
      dispatch({ type: GET_DEEP_DIVE_REQUESTED });
      const deepDive = action.payload;
      Promise.all(
        [deepDive.front_cover, ...deepDive.stories]
          .filter(value => value)
          .map(storyId => dispatch(getContentAsync(storyId)))
      ).then(actions => {
        if (actions.filter(({ type }) => type === GET_CONTENT_FAILED).length) {
          // TODO: post-mvp, handle this better
          dispatch({ type: GET_DEEP_DIVE_FAILED });
        } else {
          dispatch(initializeDeepDiveForm(hydrateDeepDiveFormUtils(deepDive)));
          dispatch(loadDeepDiveStories(actions.map(a => a.payload)));
          dispatch({ type: GET_DEEP_DIVE_SUCCEEDED });
        }
      });
    });
  };
};

export const hydrateDeepDiveFormUtils = deepDive => {
  return {
    errors: {},
    ...deepDive,
  };
};

export const UPDATE_DEEP_DIVE_FORM_AT = "UPDATE_DEEP_DIVE_FORM_AT";

export const updateDeepDiveFormAt = (path, value) => {
  return {
    type: UPDATE_DEEP_DIVE_FORM_AT,
    payload: {
      path,
      value,
    },
  };
};

export const UPDATE_DEEP_DIVE_FORM = "UPDATE_DEEP_DIVE_FORM";

export const updateDeepDiveForm = payload => {
  return {
    type: UPDATE_DEEP_DIVE_FORM,
    payload,
  };
};

export const DEEP_DIVE_EDITOR_SAVE_REQUESTED =
  "DEEP_DIVE_EDITOR_SAVE_REQUESTED";
export const DEEP_DIVE_EDITOR_SAVE_SUCCEEDED =
  "DEEP_DIVE_EDITOR_SAVE_SUCCEEDED";
export const DEEP_DIVE_EDITOR_SAVE_FAILED = "DEEP_DIVE_EDITOR_SAVE_FAILED";

export const deepDiveEditorSaveRequested = () => {
  return {
    type: DEEP_DIVE_EDITOR_SAVE_REQUESTED,
  };
};

export const deepDiveEditorSaveSuccess = () => {
  return {
    type: DEEP_DIVE_EDITOR_SAVE_SUCCEEDED,
  };
};

export const deepDiveEditorSaveFailure = error => {
  return {
    type: DEEP_DIVE_EDITOR_SAVE_FAILED,
    payload: error,
    error: true,
  };
};

export const saveDeepDiveForm = (rethrowError = false) => {
  return (dispatch, getState, getClient) => {
    const client = getClient(dispatch, getState);
    const {
      deepDiveEditor: { form },
    } = getState();

    dispatch(deepDiveEditorSaveRequested());

    const requests = [];

    if (form.id) {
      // If the form has an id, it's been saved once before, so add a content update to the request queue
      requests.push(client.deepDives.update(form.id, form));
    } else {
      // If the form does not have an ID, the content needs to be created.
      requests.push(
        client.deepDives
          .create(form)
          .then(response => dispatch(updateDeepDiveForm({ id: response.id })))
      );
    }

    return Promise.all(requests)
      .then(payloads => {
        const itemPayload = payloads[0];

        if (!form.slug && itemPayload.slug) {
          dispatch(updateDeepDiveFormAt("slug", itemPayload.slug));
        }

        dispatch(deepDiveEditorSaveSuccess());
      })
      .catch(error => {
        if (
          error.context &&
          error.context.decodedText &&
          error.context.decodedText.includes("IntegrityError") &&
          error.context.decodedText.includes("content_deepdive_slug")
        ) {
          error.message = `A Deep Dive with slug "${form.slug}" already exists`;
        }

        if (error.context && error.context.decodedJSON) {
          for (const { key, message } of [
            {
              key: "slug",
              message: `A Deep Dive with slug "${form.slug}" already exists`,
            },
            {
              key: "front_cover",
              message: "The front cover must be unique.",
            },
          ]) {
            if (error.context.decodedJSON.hasOwnProperty(key)) {
              error.context.decodedJSON[key] = message;
              error.message = message;
            }
          }
        }

        Log.logger("Save failed", "error", error);
        dispatch(
          setMessage(
            new Error(error.message ? error.message : VALIDATION_MESSAGE)
          )
        );
        logLocally(error);
        dispatch(
          deepDiveEditorSaveFailure(
            error.context ? error.context.decodedJSON : {}
          )
        );
        if (rethrowError) {
          throw error;
        }
      });
  };
};

export const VALIDATE_DEEP_DIVE_FORM_FAILED = "VALIDATE_DEEP_DIVE_FORM_FAILED";
export const validateDeepDiveFormFailed = payload => ({
  type: VALIDATE_DEEP_DIVE_FORM_FAILED,
  payload,
});

export const VALIDATE_DEEP_DIVE_FORM_SUCCEEDED =
  "VALIDATE_DEEP_DIVE_FORM_SUCCEEDED";
export const validateDeepDiveFormSucceeded = () => ({
  type: VALIDATE_DEEP_DIVE_FORM_SUCCEEDED,
});

export const validateDeepDiveFormAsync = formCandidate => {
  return dispatch => {
    const validationErrs = validate(formCandidate);

    if (validationErrs) {
      dispatch(validateDeepDiveFormFailed(validationErrs));
      return Promise.reject(validationErrs);
    } else {
      dispatch(validateDeepDiveFormSucceeded());
      return Promise.resolve();
    }
  };
};

export const PUBLISH_DEEP_DIVE_FORM_REQUESTED =
  "PUBLISH_DEEP_DIVE_FORM_REQUESTED";
export const PUBLISH_DEEP_DIVE_FORM_SUCCEEDED =
  "PUBLISH_DEEP_DIVE_FORM_SUCCEEDED";
export const PUBLISH_DEEP_DIVE_FORM_FAILED = "PUBLISH_DEEP_DIVE_FORM_FAILED";

export const publishDeepDiveFormAsync = id =>
  createAsyncAction("deepDives", "publish", "PUBLISH_DEEP_DIVE_FORM")(id);

export const DELETE_DEEP_DIVE_FORM_REQUESTED =
  "DELETE_DEEP_DIVE_FORM_REQUESTED";
export const DELETE_DEEP_DIVE_FORM_SUCCEEDED =
  "DELETE_DEEP_DIVE_FORM_SUCCEEDED";
export const DELETE_DEEP_DIVE_FORM_FAILED = "DELETE_DEEP_DIVE_FORM_FAILED";

export const deleteDeepDiveFormAsync = id =>
  createAsyncAction("deepDives", "delete", "DELETE_DEEP_DIVE_FORM")(id);

export const UNPUBLISH_DEEP_DIVE_FORM_REQUESTED =
  "UNPUBLISH_DEEP_DIVE_FORM_REQUESTED";
export const UNPUBLISH_DEEP_DIVE_FORM_SUCCEEDED =
  "UNPUBLISH_DEEP_DIVE_FORM_SUCCEEDED";
export const UNPUBLISH_DEEP_DIVE_FORM_FAILED =
  "UNPUBLISH_DEEP_DIVE_FORM_FAILED";

export const unpublishDeepDiveFormAsync = id =>
  createAsyncAction("deepDives", "unpublish", "UNPUBLISH_DEEP_DIVE_FORM")(id);

export const onChangeField = (
  fieldName,
  isBlur = false
) => dispatch => value => {
  switch (fieldName) {
    case "slug":
      return isBlur
        ? onBlurSlug(value)(dispatch)
        : onChangeSlug(value)(dispatch);
    case "stories":
      return isBlur
        ? onBlurStories(value)(dispatch)
        : onChangeStories(value)(dispatch);
    case "front_cover":
      return dispatch(updateDeepDiveFormAt(fieldName, value));
    case "series":
      return dispatch(updateDeepDiveFormAt(fieldName, value.slug));
    case "numbering_override":
      // remove empty values from object
      return dispatch(
        updateDeepDiveFormAt(
          fieldName,
          Object.keys(value).reduce(
            (acc, item) =>
              value[item] ? { ...acc, [item]: value[item] } : acc,
            {}
          )
        )
      );
    default:
      return onChangeTextField(fieldName)(value)(dispatch);
  }
};

export const onBlurSlug = value => dispatch => {
  dispatch(
    updateDeepDiveFormAt(
      "slug",
      sanitizeSlug(value.target.value, {
        removeTrailingDashes: true,
        useNullForEmpty: true,
      })
    )
  );
};

export const onChangeSlug = value => dispatch => {
  dispatch(
    updateDeepDiveFormAt(
      "slug",
      sanitizeSlug(value.target.value, { useNullForEmpty: true })
    )
  );
};

export const onChangeTextField = fieldName => value => dispatch => {
  dispatch(
    updateDeepDiveFormAt(fieldName, value.target ? value.target.value : value)
  );
};

export const onBlurStories = value => dispatch => {
  dispatch(
    updateDeepDiveFormAt(
      "stories",
      value.target.value
        .split(/,|\n/)
        .filter(s => s.match(UUID_REGEXP))
        .map(s => s.match(UUID_REGEXP)[0])
    )
  );
};

export const onChangeStories = value => dispatch => {
  dispatch(updateDeepDiveFormAt("stories", value));
};
